import noop from 'lodash/noop';
import { DatePickerField, InputField, RadioListField } from 'lib/iterable-hook-form/FormField';
import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import styled from 'styled-components';
import { ErrorText } from 'ui/formField';
import { RadioListVariantProps } from 'ui/radioList';
import { body2, spacing, Subtitle2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useStartNodeContext } from '../../StartNodeEditor.context';
import { EndRecurrenceType } from './Scheduler.types';
import { parseStartTimeForSaving, getSetStartTime } from './Scheduler.utils';
import { RowWrapper } from './SchedulerForm.styled';

export const OptionText = styled.span`
  ${body2};
`;

export const EndRecurrenceTypeLabel = styled(OptionText)`
  width: ${pxToRem(46)};
`;

export const EndSendsCountInputField = styled(InputField)`
  width: ${pxToRem(145)};
  input {
    width: ${spacing.base};
  }
`;

export const EndSendsDatePickerField = styled(DatePickerField)`
  form {
    width: ${pxToRem(145)};
  }
`;

export const EndSendsRadioListField = styled(RadioListField)`
  width: 100%;
`;

const StyledErrorText = styled(ErrorText)`
  margin: 0 0 0 ${spacing.double};
`;

const EndRecurrenceOnDate = () => {
  const { isReadOnly } = useStartNodeContext();
  const [endType, minuteOfDay] = useWatch({
    name: ['endType', 'minuteOfDay'],
  });

  const { errors } = useFormState<{ endDate: { message?: string } }>({
    name: ['endDate'],
  });

  const endDateError = errors?.endDate;

  const getEndStartDate = useMemo(
    () => getSetStartTime(parseStartTimeForSaving(minuteOfDay)),
    [minuteOfDay],
  );

  return (
    <>
      <RowWrapper>
        <EndRecurrenceTypeLabel>On</EndRecurrenceTypeLabel>
        <EndSendsDatePickerField
          name="endDate"
          placeholder="Optional"
          isDisabled={endType === EndRecurrenceType.Never || isReadOnly}
          isReadOnly={isReadOnly}
          validDateRange={{ start: getEndStartDate }}
        />
      </RowWrapper>
      {endDateError?.message && (
        <StyledErrorText>
          <>{endDateError.message}</>
        </StyledErrorText>
      )}
    </>
  );
};

const END_RECURRENCE_OPTIONS: RadioListVariantProps['options'] = [
  {
    id: EndRecurrenceType.Never,
    value: EndRecurrenceType.Never,
    name: () => <OptionText>Never</OptionText>,
  },
  {
    id: EndRecurrenceType.OnDate,
    value: EndRecurrenceType.OnDate,
    name: EndRecurrenceOnDate,
  },
];

export const EndRecurrence = () => {
  const { isReadOnly } = useStartNodeContext();
  const { watch, trigger } = useFormContext();

  useEffect(() => {
    watch((_, { name }) => {
      match(name)
        .with('endType', () => {
          // validate endDate anytime endType changes
          trigger('endDate');
        })
        .otherwise(noop);
    });
  }, [watch, trigger]);

  return (
    <div>
      <Subtitle2>End</Subtitle2>
      <EndSendsRadioListField
        name="endType"
        isReadOnly={isReadOnly}
        options={END_RECURRENCE_OPTIONS}
      />
    </div>
  );
};
